/**
 * Loads `@tursodatabase/sync-wasm` on web as a NATIVE browser ES module from a
 * public asset, bypassing Metro entirely.
 *
 * Why not bundle it through Metro: `@tursodatabase/sync-wasm`'s bundle uses
 * module-scope **top-level await** (`await fetch(<inlined data: wasm>)` +
 * `await setupMainThread(...)`) plus a module `Worker`. The RN babel preset lowers
 * ESM to a SYNCHRONOUS CommonJS factory but leaves the top-level `await` in place —
 * `await` in a non-async factory body is illegal, so Metro/terser cannot represent
 * the module (verified: terser throws "Unexpected token: name (fetch)"). A dynamic
 * `await import("@tursodatabase/sync-wasm")` does NOT help: dynamic import only
 * controls *when* the module is requested; Metro still has to compile the target to
 * a sync CJS factory, which can't hold top-level await.
 *
 * The browser, however, runs the file as native ESM where top-level await,
 * `import.meta`, and module workers all work. So `web-prebuild.ts`
 * (`ensureTursoSyncWasmAssets`) mirrors the self-contained `/bundle` build (wasm
 * inlined as `data:`, worker via `createObjectURL(Blob)`, no sibling assets) to
 * `public/turso-sync-wasm/main.es.js`, and we load it here.
 *
 * Metro must NOT see the `import()` at all. It rejects a non-literal specifier
 * ("Invalid call ... import(url)") AND constant-folds a literal one then hard-errors
 * trying to resolve the public path as a module. So we hide the dynamic import from
 * Metro's transform entirely by building it through the `Function` constructor — the
 * literal `import(` token never appears in source Metro parses, and at call time the
 * browser runs a real native dynamic import. (No CSP ships for this app — web dev
 * server and EAS Hosting serve no `Content-Security-Policy`/`script-src` — so the
 * `Function` indirection is allowed.)
 *
 * Threaded wasm needs `SharedArrayBuffer`, available only in cross-origin-isolated
 * contexts. Development applies COOP/COEP to every response. EAS Hosting applies
 * them to the document but not its static assets, so production injects the Blob
 * module URL created by `create-turso-leader-worker.ts`.
 */
import type {
  Database as TursoWasmDatabase,
  DatabaseOpts,
} from "@-/com.npmjs.tursodatabase__sync-wasm";
import {
  getInjectedTursoSyncWasmModuleUrl,
  getTursoSyncWasmAssetUrl,
} from "#turso-web-worker-assets";

export type SyncWasmDatabaseCtor = new (opts: DatabaseOpts) => TursoWasmDatabase;

interface SyncWasmModule {
  Database: SyncWasmDatabaseCtor;
}

// Native dynamic import built via `Function` so the `import()` is invisible to
// Metro's transform (which otherwise rejects/over-resolves it). Created lazily and
// memoized.
type NativeImport = (specifier: string) => Promise<unknown>;
let nativeImport: NativeImport | null = null;
function getNativeImport(): NativeImport {
  // oxlint-disable-next-line no-new-func, typescript/no-unsafe-type-assertion -- intentional: hide the dynamic import from Metro's static analysis (no CSP ships); the Function body is a typed dynamic import.
  nativeImport ??= new Function("specifier", "return import(specifier);") as NativeImport;
  return nativeImport;
}

let loadPromise: Promise<SyncWasmModule> | null = null;

function isSyncWasmModule(value: unknown): value is SyncWasmModule {
  return (
    typeof value === "object" &&
    value !== null &&
    "Database" in value &&
    typeof (value as Record<"Database", unknown>).Database === "function"
  );
}

/** Reads a module namespace's `default` export without an unsafe assertion. */
function getDefaultExport(value: unknown): unknown {
  if (typeof value === "object" && value !== null && "default" in value) {
    return (value as Record<"default", unknown>).default;
  }
  return undefined;
}

/**
 * Dynamically imports the public sync-wasm bundle (once, memoized) and returns its
 * module namespace. Throws if the asset is missing (prebuild not run) or doesn't
 * expose `Database`.
 */
export async function loadSyncWasm(): Promise<SyncWasmModule> {
  loadPromise ??= (async () => {
    // Native dynamic import hidden from Metro; the browser evaluates the file as
    // ESM (top-level await + module worker run natively).
    // Production injects a Blob URL because EAS Hosting's static response lacks
    // the COEP header required by an isolated module worker. The public URL is a
    // development/direct-worker fallback.
    const url = getInjectedTursoSyncWasmModuleUrl() ?? getTursoSyncWasmAssetUrl();
    const mod: unknown = await getNativeImport()(url);
    // Native ESM may surface the namespace directly or under `default` (esm/cjs
    // interop across bundlers); unwrap a couple of levels to be safe.
    let ns: unknown = mod;
    for (let depth = 0; depth < 3 && !isSyncWasmModule(ns); depth++) {
      const next = getDefaultExport(ns);
      if (next == null) {
        break;
      }
      ns = next;
    }
    if (!isSyncWasmModule(ns)) {
      throw new TypeError(
        `sync-wasm bundle at ${url} did not expose a Database export ` +
          "(run the web prebuild so public/turso-sync-wasm/main.es.js exists)",
      );
    }
    return ns;
  })();
  try {
    return await loadPromise;
  } catch (error) {
    // Reset so a transient failure (e.g. asset not yet served) can be retried on
    // the next open instead of caching the rejection forever.
    loadPromise = null;
    throw error;
  }
}
